import React, { Children, type ReactElement, type ReactNode } from 'react';

import logger from 'Utils/logger';

import { AllowableVarReplacement } from './types';

type StringMapping = Record<string, AllowableVarReplacement>;

function isReactElement(node: ReactNode): node is ReactElement {
  return React.isValidElement(node);
}

function isIterable(node: ReactNode): node is Iterable<ReactNode> {
  return typeof node === 'object' && node !== null && Symbol.iterator in node;
}

export function replaceVarsInNode(
  node: ReactNode,
  vars: StringMapping,
  field: string | null,
  depth = 0,
): ReactNode {
  if (typeof node === 'string') {
    const trimmed = node.trim();
    if (trimmed[0] === '{' && trimmed[trimmed.length - 1] === '}') {
      const variableName = trimmed.slice(1, -1);
      if (vars[variableName] !== undefined) {
        return vars[variableName];
      }
      logger.error(`Variable {${variableName}} not found in vars mapping for field "${field}"`);
      return node;
    }
  } else if (isIterable(node)) {
    return Array.from(node).map((child) => replaceVarsInNode(child, vars, field, depth + 1));
  } else if (isReactElement(node) && node.props.children.length > 0) {
    // If the node has children, recursively replace variables in each child
    return React.cloneElement(
      node,
      {},
      Children.map(node.props.children, (child) => replaceVarsInNode(child, vars, field, depth + 1)),
    );
  }
  return node;
}
